import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, ArrowUpDown, ShoppingBag, Star, Zap, X, Store } from 'lucide-react';
import api from '../utils/api';
import { resolveImageUrl } from '../utils/images';
import { Loader2 } from 'lucide-react';

export const Home = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // États
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recent');
  const [heroCollapsed, setHeroCollapsed] = useState(false);
  
  // Auto-repli du HERO
  useEffect(() => {
    const timer = setTimeout(() => setHeroCollapsed(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => { fetchCategories(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => { fetchArticles(); updateUrl(); }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, sortBy]);

  const updateUrl = () => {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
    if (sortBy && sortBy !== 'recent') params.sort = sortBy;
    setSearchParams(params);
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Erreur catégories');
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory && selectedCategory !== 'all') params.append('category_id', selectedCategory);
      params.append('sort', sortBy);
      params.append('limit', '60'); 

      const response = await api.get(`/articles?${params}`);
      setArticles(response.data.articles);
    } catch (error) {
      console.error('Erreur articles');
    }
    setLoading(false);
  };

  const calculateDiscount = (price, referencePrice) => {
    if (!referencePrice || referencePrice <= price) return 0;
    return Math.round(((referencePrice - price) / referencePrice) * 100);
  };

  // Helper pour afficher les étoiles compactes
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            size={8} 
            className={`${i < Math.round(rating) ? 'fill-orange-500 text-orange-500' : 'text-zinc-700'}`} 
          />
        ))}
        <span className="text-[8px] text-zinc-400 ml-1 font-medium">({rating})</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen dp-bg font-sans selection:bg-orange-500/30">
      
      {/* --- HERO SECTION: COMPACTE & SOBRE --- */}
      <div className={`overflow-hidden transition-all duration-500 ease-in-out border-b border-[hsl(var(--border))] bg-[hsl(var(--bg))] ${
        heroCollapsed ? 'max-h-0 opacity-0 border-none' : 'max-h-[250px] opacity-100'
      }`}>
        <div className="container mx-auto px-4 py-6 relative">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-[0.2em] text-orange-500 mb-2">
              <Zap size={10} className="fill-orange-500" /> Offres du jour
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tighter text-[hsl(var(--text))] uppercase italic mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Les Bons Plans <span className="text-orange-500">DownPricer</span>
            </h1>
            <p className="dp-muted text-xs max-w-lg mx-auto font-medium">
              Achetez malin, négociez directement. Le marketplace S-Tier.
            </p>
            <button 
              onClick={() => setHeroCollapsed(true)}
              className="absolute top-4 right-4 p-1 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-2 md:px-4 py-4 pb-24">
        
        {/* --- FILTRES --- */}
        <div className="sticky top-2 z-30 mb-4 flex flex-col md:flex-row gap-2 items-center justify-between bg-[hsl(var(--surface))] backdrop-blur-md p-2 border border-[hsl(var(--border))] rounded-lg shadow-xl">
          <div className="relative w-full md:w-80 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--text-muted))] group-focus-within:text-orange-500 transition-colors" />
            <Input
              placeholder="Chercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 dp-input focus:border-orange-500/50 focus:ring-0 h-9 rounded-md text-xs font-medium"
            />
          </div>

          <div className="flex gap-2 w-full md:w-auto overflow-x-auto no-scrollbar">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[130px] dp-input h-9 rounded-md text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--text-muted))] focus:ring-0">
                <div className="flex items-center gap-2 truncate">
                <Filter className="h-3 w-3 text-[hsl(var(--text-muted))]" />
                  <SelectValue placeholder="Catégorie" />
                </div>
              </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text))]">
                <SelectItem value="all">Toutes</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[130px] dp-input h-9 rounded-md text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--text-muted))] focus:ring-0">
                <div className="flex items-center gap-2">
                <ArrowUpDown className="h-3 w-3 text-[hsl(var(--text-muted))]" />
                  <SelectValue />
                </div>
              </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text))]">
                <SelectItem value="recent">Plus récents</SelectItem>
                <SelectItem value="price_low">Prix croissant</SelectItem>
                <SelectItem value="views">Populaires</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* --- GRID COMPACTE --- */}
        {loading ? (
          <ArticlesSkeleton />
        ) : articles.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3">
              {articles.map((article) => {
                if (!article) return null;
                const discount = calculateDiscount(article.price, article.reference_price);
                const imageUrl = resolveImageUrl(article.photos?.[0]);
                const vendor = article.vendor;
                const vendorName = vendor?.seller_name || vendor?.minisite_name || 'Boutique';

                return (
                  <Card
                    key={article.id}
                    className="group dp-surface rounded-lg overflow-hidden hover:border-orange-500/40 transition-all duration-200 cursor-pointer flex flex-col h-full"
                    onClick={() => navigate(`/article/${article.id}`)}
                  >
                    {/* Image Zone */}
                    <div className="relative aspect-square bg-[hsl(var(--surface-2))] overflow-hidden">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={article.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="h-6 w-6 text-[hsl(var(--text-muted))]" />
                        </div>
                      )}
                      
                      {/* Badge Vendeur Tiers (Modifié) */}
                      {(article.is_third_party || article.source === 'minisite') && (
                        <div className="absolute top-0 left-0 bg-[hsl(var(--surface))] backdrop-blur-sm px-1.5 py-0.5 rounded-br-lg border-r border-b border-[hsl(var(--border))]">
                          <div className="flex items-center gap-1">
                            <Store size={8} className="text-orange-500" />
                            <span className="text-[7px] font-black uppercase text-[hsl(var(--text))] tracking-wider">Vendeur tiers</span>
                          </div>
                        </div>
                      )}

                      {/* Badge Réduction */}
                      {discount > 0 && (
                        <div className="absolute top-0 right-0 bg-red-600 px-1.5 py-0.5 rounded-bl-lg">
                          <span className="text-[9px] font-black text-white">-{discount}%</span>
                        </div>
                      )}
                    </div>

                    {/* Contenu Compact */}
                    <CardContent className="p-2.5 flex-grow flex flex-col justify-between">
                      <div>
                        {/* Titre */}
                        <h3 className="text-[11px] md:text-xs font-bold text-zinc-200 leading-tight mb-1 line-clamp-2 h-8 group-hover:text-orange-500 transition-colors">
                          {article.name}
                        </h3>
                        
                        {/* Ligne Vendeur + Notation (Restaurée) */}
                        {article.is_third_party && vendor ? (
                          <div className="flex flex-col gap-0.5 mb-2" onClick={(e) => e.stopPropagation()}>
                            <button 
                              className="text-[9px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] transition-colors flex items-center gap-1 w-fit"
                              onClick={() => navigate(`/s/${vendor.minisite_slug}`)}
                            >
                              <span className="truncate max-w-[80px]">Par {vendorName}</span>
                            </button>
                            {/* Affichage des étoiles */}
                            {vendor.rating_avg > 0 && renderStars(vendor.rating_avg)}
                          </div>
                        ) : (
                        <div className="h-6 mb-1" /> // Spacer
                        )}
                      </div>

                      {/* Prix */}
                      <div className="flex items-end gap-1 mt-1 pt-1 border-t border-[hsl(var(--border))]">
                        <div className="flex items-start text-[hsl(var(--text))]">
                          <span className="text-[10px] font-bold mt-[2px]">€</span>
                          <span className="text-lg md:text-xl font-black tracking-tighter leading-none">
                            {Math.floor(article.price)}
                          </span>
                          <span className="text-[9px] font-bold mt-[2px]">
                            {Math.round((article.price % 1) * 100).toString().padEnd(2, '0')}
                          </span>
                        </div>

                        {discount > 0 && (
                          <span className="text-[9px] text-[hsl(var(--text-muted))] line-through mb-[2px] ml-1">
                            {article.reference_price}€
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <div className="mt-8">
              <RequestDealCTA />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <Search className="h-8 w-8 text-[hsl(var(--text-muted))] mb-2" />
    <p className="text-[hsl(var(--text-muted))] text-xs font-bold uppercase tracking-widest mb-6">Aucun résultat</p>
    <RequestDealCTA centered />
  </div>
);

const RequestDealCTA = ({ centered = false }) => (
  <div className={`w-full max-w-xl ${centered ? 'mx-auto' : ''}`}>
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 md:p-5 text-center">
      <p className="text-[hsl(var(--text))] text-sm md:text-base font-semibold mb-3">
        Rien ne vous convient ? Faites une demande de prix cassé !
      </p>
      <Link
        to="/faire-demande"
        className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white text-xs md:text-sm font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition-colors"
      >
        Faire une demande
      </Link>
    </div>
  </div>
);

const ArticlesSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3">
    {[...Array(12)].map((_, i) => (
      <div key={i} className="bg-[hsl(var(--surface))] rounded-lg overflow-hidden border border-[hsl(var(--border))] flex flex-col h-64">
        <div className="aspect-square bg-[hsl(var(--surface-2))] animate-pulse" />
        <div className="p-2 space-y-2 flex-1">
          <div className="h-3 bg-[hsl(var(--surface-2))] rounded w-3/4 animate-pulse" />
          <div className="h-2 bg-[hsl(var(--surface-2))] rounded w-1/2 animate-pulse" />
          <div className="mt-auto h-5 bg-[hsl(var(--surface-2))] rounded w-12 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);